import { useState } from "react";
import { useTranslation } from "react-i18next";
import ExcelJS from "exceljs";
import { useParams } from "react-router-dom";
import { chunk } from "lodash";
import { format } from "date-fns";
import {
  SummaryColumns,
  Tables,
  Totals,
  Rounding,
  roundFloat,
  mapQuillDeltaToRichText,
  mapHtmlToRichText,
} from "@repo/utils";
import { downloadFile, getProposalExportFileName } from "@utils";
import { useStores } from "@hooks"
import { useEstimateEditorSettings, useEstimateEditorStore, useEstimateStructure } from "@tools";
import { TITLE_ROW_HEIGHT } from "./xlsStyleUtils";
import { useXlsStyles } from "./useXlsStyles";
import { capitalize } from "@material-ui/core";

export const useXlsExporter = () => {
  const { estimateUuid, company, project } = useParams();
  const { userStore } = useStores();
  const { isSellerOrClient } = useEstimateEditorSettings();
  const { estimateName, renderedTextDescription } = useEstimateEditorStore();
  const structure = useEstimateStructure();
  const { t } = useTranslation();
  
  const {
    dividerStyle,
    proposalHeaderStyle,
    tableHeaderStyle,
    taskFont,
    taskStyle,
    totalCellStyle,
    priceModifierDividerStyle,
    valueCellStyle,
    turnedOffValueCellStyle,
    titleStyle,
  } = useXlsStyles();
  
  const [ isLoading, setLoading ] = useState(false);
  
  const {
    settings,
    workTypes,
    priceModifiers,
    timelineWorkTypes,
    displayTwoValues,
    displayTotalPrice,
    displayTotalPriceMax,
    displayTotalHours,
    displayTotalHoursMax,
    displayTotalTime,
    displayTotalTimeMax,
    showMaxTotalPrice,
    showMaxTotalHours,
    showMaxTotalTime,
    displaySections,
    breakdownWorkTypes,
    hasBreakdownFixedPrice,
    hasBreakdownMaterialPrice,
    displayBreakdownMaterialPrice,
    displayBreakdownFixedPrice,
    displayBreakdownMaxFixedPrice,
    showBreakdownMaxFixedPrice,
    sectionsHaveAnyDescriptions,
    sectionDescriptionsKeys,
    totalDevelopmentTime,
    timelineBilling,
    textDescription,
    showBreakdownPriceColumn,
    showBreakdownEstimatesColumn,
    showBreakdownLabelsColumn,
  } = structure;
  
  const {
    useMinMax,
    currencyObj,
    timeModifier,
    viewLevel,
    valueLevel,
    modifier,
    roundPrice,
    usedTotals,
    visibleTables,
    visibleSummaryColumns,
    usedRoundDecimals,
    turnOffFixedPrice,
    turnOffMaterials,
  } = settings;
  
  const exportProposal = async (unstyled=false) => {
    
    setLoading(true);
    
    const workbook = new ExcelJS.Workbook();
    
    workbook.creator = userStore.data.fullname;
    workbook.created = new Date();
    workbook.modified = new Date();
    
    const worksheet = workbook.addWorksheet(t("views.editor.xls_worksheet"));
    
    const showNumerationColumn = Boolean(displaySections.find(s => s.showNumerationColumn));
    
    const titleCols = showNumerationColumn ? 5 : 4;
    
    let summaryPriceColIndex;
    const totalSummaryCols = visibleSummaryColumns.reduce((t, col) => {
      if(col.name === SummaryColumns.COST)
        summaryPriceColIndex = t;
        
      return t + (useMinMax && ![SummaryColumns.RATE, SummaryColumns.TEAM].includes(col.name) ? 2 : 1)
    }, 0);
    
    const breakdownLabelCols = showBreakdownLabelsColumn ? 2 : 0;
    const breakdownDescriptionCols = sectionsHaveAnyDescriptions
      ? sectionDescriptionsKeys.length * 3
      : 0;
    const breakdownEstimateCols = showBreakdownEstimatesColumn && valueLevel >= 0
      ? breakdownWorkTypes.length * (useMinMax ? 2 : 1)
      : 0
    const breakdownPriceCols = (valueLevel >= 0 && showBreakdownPriceColumn && isSellerOrClient ? (useMinMax ? 2 : 1) : 0);
    
    const totalBreakdownCols = breakdownLabelCols + breakdownDescriptionCols + breakdownEstimateCols + breakdownPriceCols;
    
    // =========== COLUMN SETTINGS ===========
    for(let i = 1; i < titleCols; i++)
      worksheet.getColumn(i).width = 5;
    
    if(showNumerationColumn)
      worksheet.getColumn(1).width = 10;
    
    worksheet.getColumn(titleCols).width = 50;
    
    for (let i = 0; i < Math.max(totalSummaryCols, totalBreakdownCols, totalDevelopmentTime); i++)
      worksheet.getColumn(titleCols + 1 + i).width = 12;
    
    
    let currentCol = 1;
    let currentRow = 1;
    let cell;
    
    // =========== ESTIMATE TITLE & EXPORT DATE ===========
    worksheet.getRow(currentRow).height = TITLE_ROW_HEIGHT; // summary row
    cell = worksheet.getCell(cti(currentCol, currentRow))
    cell.value = estimateName;
    cell.style = titleStyle(!unstyled);
    worksheet.mergeCells(
      `${cell.address}:${cti(titleCols, currentRow)}`
    );
    currentCol += titleCols;
    
    cell = worksheet.getCell(cti(currentCol, currentRow))
    cell.value = format(new Date, "dd-MM-yyyy, hh-mm");
    cell.style = titleStyle(!unstyled);
    worksheet.mergeCells(
      `${cell.address}:${cti(titleCols + totalSummaryCols, currentRow)}`
    );
    currentRow++;
    
    // =========== SUMMARY ===========
    const buildTableSummary = () => {
      currentCol = 1;
      
      // =========== ROW SETTINGS ===========
      worksheet.getRow(currentRow).height = TITLE_ROW_HEIGHT; // summary row
      worksheet.getRow(currentRow + 1).height = useMinMax
        ? TITLE_ROW_HEIGHT
        : 36; // summary table header row
      
      // =========== SUMMARY HEADERS ===========
      cell = worksheet.getCell(cti(currentCol, currentRow))
      cell.value = t("proposal_parts.editor_sections.summary");
      cell.style = proposalHeaderStyle(!unstyled);
      worksheet.mergeCells(
        `${cell.address}:${cti(titleCols + totalSummaryCols, currentRow)}`
      );
      currentRow++;
      
      // --------- work type ---------
      cell = worksheet.getCell(cti(currentCol, currentRow))
      cell.value = t("common.work_type");
      cell.style = tableHeaderStyle(!unstyled);
      worksheet.mergeCells(`${cell.address}:${cti(titleCols, currentRow)}`);
      currentCol += titleCols;
      
      visibleSummaryColumns.forEach(({ name }) => {
        switch (name) {
          case SummaryColumns.COST:
            if (isSellerOrClient) {
              cell = worksheet.getCell(cti(currentCol, currentRow));
              cell.value = t("views.editor.summary_table.cost_currency", { currency: currencyObj.symbolStart || currencyObj.symbolEnd } );
              cell.style = tableHeaderStyle(!unstyled, true);
              if (useMinMax) {
                worksheet.mergeCells(`${cell.address}:${cti(currentCol + 1, currentRow)}`);
                currentCol++;
              }
            }
            break;
          case SummaryColumns.HOURS:
            cell = worksheet.getCell(cti(currentCol, currentRow));
            cell.value = t(`times.${timeModifier === 1 ? "hour" : "day"}`);
            cell.style = tableHeaderStyle(!unstyled, true);
            if (useMinMax) {
              worksheet.mergeCells(`${cell.address}:${cti(currentCol + 1, currentRow)}`);
              currentCol++;
            }
            break;
          case SummaryColumns.RATE:
            if (isSellerOrClient) {
              cell = worksheet.getCell(cti(currentCol, currentRow));
              cell.value = `${t(`views.editor.summary_table.rate_${ timeModifier === 1 ? "hours" : "days" }`)} (${currencyObj.code})`;
              cell.style = tableHeaderStyle(!unstyled, true);
            }
            break;
          case SummaryColumns.TEAM:
            cell = worksheet.getCell(cti(currentCol, currentRow));
            cell.value = t("views.editor.summary_table.team_members");
            cell.style = tableHeaderStyle(!unstyled, true);
            break;
          case SummaryColumns.TIME:
            cell = worksheet.getCell(cti(currentCol, currentRow));
            cell.value = t("views.editor.summary_table.time");
            cell.style = tableHeaderStyle(!unstyled, true);
            if (useMinMax) {
              worksheet.mergeCells(
                `${cell.address}:${cti(currentCol + 1, currentRow)}`
              );
              currentCol++;
            }
        }
        currentCol++;
      });
      
      currentRow++;
      
      // =========== SUMMARY ROWS ===========
      const setSummaryValues = ( element, level, showOnlyPrice, showPrice, currentCol ) => {
        const {
          name,
          displayHours,
          displayHoursMax,
          displayPrice,
          displayPriceMax,
          displayTime,
          displayTimeMax,
          displayRate,
          showMaxPrice,
          showMaxHours,
          showMaxTime,
          teamMembers,
          totalTeamMembers,
          turnOff,
        } = element;
        
        currentCol = 1 + level;
        
        cell = worksheet.getCell(cti(currentCol + level, currentRow));
        worksheet.mergeCells(`${cell.address}:${cti(titleCols, currentRow)}`);
        cell.value = name;
        cell.style = taskFont(!unstyled, 0, turnOff);
        
        currentCol = 1 + titleCols;
        
        visibleSummaryColumns.forEach(({ name }) => {
          switch (name) {
            case SummaryColumns.COST:
              if (isSellerOrClient) {
                cell = worksheet.getCell(cti(currentCol, currentRow));
                cell.style = valueCellStyle(!unstyled, false, undefined, turnOff);
                if (showPrice)
                  cell.value = displayPrice;
                
                if (displayTwoValues) {
                  currentCol++;
                  const cellMax = worksheet.getCell(
                    cti(currentCol, currentRow)
                  );
                  if (showMaxPrice) {
                    if (showPrice)
                      cellMax.value = displayPriceMax;
                    cell.style = valueCellStyle(!unstyled, false, "left", turnOff);
                    cellMax.style = valueCellStyle(!unstyled, false, "right", turnOff);
                  } else
                    worksheet.mergeCells(`${cell.address}:${cellMax.address}`);
                }
              }
              break;
            case SummaryColumns.HOURS:
              cell = worksheet.getCell(cti(currentCol, currentRow));
              cell.style = valueCellStyle(!unstyled, false, undefined, turnOff);
              if (showOnlyPrice)
                cell.value = roundFloat(displayHours, Rounding.FULL, false, usedRoundDecimals);
              
              if (displayTwoValues) {
                currentCol++;
                const cellMax = worksheet.getCell(cti(currentCol, currentRow));
                if (showMaxHours) {
                  cell.style = valueCellStyle(!unstyled, false, "left", turnOff);
                  cellMax.style = valueCellStyle(!unstyled, false, "right", turnOff);
                  if (showOnlyPrice) cellMax.value = roundFloat(displayHoursMax, Rounding.FULL, false, usedRoundDecimals);
                } else
                  worksheet.mergeCells(`${cell.address}:${cellMax.address}`);
              }
              break;
            case SummaryColumns.RATE:
              if (isSellerOrClient) {
                cell = worksheet.getCell(cti(currentCol, currentRow));
                cell.style = valueCellStyle(!unstyled, false, undefined, turnOff);
                if (showOnlyPrice) cell.value = displayRate;
              }
              break;
            case SummaryColumns.TEAM:
              cell = worksheet.getCell(cti(currentCol, currentRow));
              cell.style = valueCellStyle(!unstyled, false, undefined, turnOff);
              if (showOnlyPrice)
                cell.value = totalTeamMembers || teamMembers || 1;
              break;
            case SummaryColumns.TIME:
              cell = worksheet.getCell(cti(currentCol, currentRow));
              cell.style = valueCellStyle(!unstyled, false, undefined, turnOff);
              if (showOnlyPrice)
                cell.value = displayTime;
              
              if (displayTwoValues) {
                currentCol++;
                const cellMax = worksheet.getCell(cti(currentCol, currentRow));
                if(showMaxTime) {
                  cellMax.style = valueCellStyle(!unstyled, false, undefined, turnOff);
                  if (showOnlyPrice)
                    cellMax.value = displayTimeMax;
                } else
                  worksheet.mergeCells(`${cell.address}:${cellMax.address}`);
              }
          }
          currentCol++;
        });
        
        currentRow++;
      };
      
      workTypes.forEach((workType) => {
        const { hasFixedPrice, isOpened, resources, hasResources } = workType;
        
        const showPrice = !hasResources || !isOpened;
        // const showOnlyPrice = showPrice && (!hasFixedPrice || inBreakdown);
        const showOnlyPrice = showPrice && !hasFixedPrice;
        
        setSummaryValues(workType, 0, showOnlyPrice, showPrice, currentCol);
        
        if (isOpened)
          resources?.forEach((resource) => {
            setSummaryValues(resource, 1, true, true, currentCol);
          });
      });
      
      const setBreakdownPriceRows = (title, turnOff, value, valueMax, showMax) => {
        currentCol = 1;
        cell = worksheet.getCell(`${cti(currentCol, currentRow)}`);
        cell.value = title;
        cell.style = taskFont(!unstyled, 0, turnOff);
        
        if(typeof summaryPriceColIndex === "number") {
          worksheet.mergeCells(`${cell.address}:${cti(titleCols + summaryPriceColIndex, currentRow)}`);
          currentCol += titleCols + summaryPriceColIndex;
          
          if (isSellerOrClient) {
            cell = worksheet.getCell(cti(currentCol, currentRow));
            cell.style = valueCellStyle(!unstyled, false, undefined, turnOff);
            cell.value = value;
            currentCol++;
            
            if (useMinMax) {
              const cellMax = worksheet.getCell(cti(currentCol, currentRow));
              if (useMinMax && showMax) {
                cellMax.value = valueMax;
                cell.style = valueCellStyle(!unstyled, false, "left", turnOff);
                cellMax.style = valueCellStyle(!unstyled, false, "right", turnOff);
              } else
                worksheet.mergeCells(`${cell.address}:${cellMax.address}`);
            }
            currentCol++;
          }
        }
        
        if(currentCol < titleCols + totalSummaryCols)
          worksheet.mergeCells(`${cti(currentCol, currentRow)}:${cti(titleCols + totalSummaryCols, currentRow)}`);
        
        currentRow++;
      }
      
      if (hasBreakdownFixedPrice)
        setBreakdownPriceRows(
          t("views.editor.summary_table.fixed_costs"),
          turnOffFixedPrice,
          displayBreakdownFixedPrice,
          displayBreakdownMaxFixedPrice,
          showBreakdownMaxFixedPrice,
        )
      
      if (hasBreakdownMaterialPrice)
        setBreakdownPriceRows(
          t("views.editor.summary_table.materials"),
          turnOffMaterials,
          displayBreakdownMaterialPrice,
        )
      
      // =========== DISCOUNTS & OVERHEADS ===========
      if(priceModifiers?.length) {
        currentCol = 1;
        cell = worksheet.getCell(cti(currentCol, currentRow))
        worksheet.mergeCells(`${cell.address}:${cti(titleCols + totalSummaryCols, currentRow)}`);
        cell.style = priceModifierDividerStyle(!unstyled);
        currentRow++;
        
        priceModifiers.forEach((priceModifier) => {
          setSummaryValues(priceModifier, 0, true, true, currentCol);
        });
      }
      
      // =========== TOTALS ===========
      if (workTypes.length && isSellerOrClient) {
        let currentCol = titleCols;
        
        cell = worksheet.getCell(cti(1, currentRow));
        worksheet.mergeCells(`${cell.address}:${cti(currentCol, currentRow)}`);
        cell.value = t(`views.editor.summary_table.totals.title`);
        cell.style = totalCellStyle(!unstyled, true);
        currentCol++;
        
        visibleSummaryColumns.forEach(({ name }) => {
          cell = worksheet.getCell(cti(currentCol, currentRow));
          cell.style = totalCellStyle(!unstyled);
          
          const usesTotalCost = usedTotals?.find(({ name }) => name === Totals.COST)?.visible;
          const usesTotalHours = usedTotals?.find(({ name }) => name === Totals.HOURS)?.visible;
          const usesTotalTime = usedTotals?.find(({ name }) => name === Totals.TIME)?.visible;
          
          switch ( name ) {
            case SummaryColumns.COST:
              if ( isSellerOrClient && usesTotalCost )
                cell.value = displayTotalPrice;
              break;
            case SummaryColumns.HOURS:
              if ( usesTotalHours )
                cell.value = displayTotalHours;
              break;
            case SummaryColumns.TIME:
              if ( usesTotalTime )
                cell.value = displayTotalTime;
              break;
          }
          currentCol++;
          
          if(displayTwoValues && ![SummaryColumns.TEAM, SummaryColumns.RATE].includes(name)) {
            const cellMax = worksheet.getCell(cti(currentCol, currentRow));
            cellMax.style = totalCellStyle(!unstyled);
            switch ( name ) {
              case SummaryColumns.COST:
                if ( isSellerOrClient && usesTotalCost && showMaxTotalPrice )
                  cellMax.value = displayTotalPriceMax;
                else worksheet.mergeCells(`${cell.address}:${cellMax.address}`);
                break;
              case SummaryColumns.HOURS:
                if ( usesTotalHours && showMaxTotalHours )
                  cellMax.value = displayTotalHoursMax;
                else worksheet.mergeCells(`${cell.address}:${cellMax.address}`);
                break;
              case SummaryColumns.TIME:
                if ( usesTotalTime && showMaxTotalTime )
                  cellMax.value = displayTotalTimeMax;
                else worksheet.mergeCells(`${cell.address}:${cellMax.address}`);
                break;
            }
            currentCol++;
          }
        });
        
        currentRow++;
      }
    };
    
    // =========== TEXT DESCRIPTION ===========
    const buildTextDescription = () => {
      if(!renderedTextDescription) return;
      
      currentCol = 1;
      const lastColl = titleCols + totalSummaryCols;
      
      // =========== row settings ===========
      worksheet.getRow(currentRow).height = TITLE_ROW_HEIGHT; // summary row
      worksheet.getRow(currentRow + 1).height = useMinMax
        ? TITLE_ROW_HEIGHT
        : 36; // summary table header row
      
      // =========== header ===========
      cell = worksheet.getCell(cti(currentCol, currentRow));
      cell.value = t("proposal_parts.editor_sections.description");
      cell.style = proposalHeaderStyle(!unstyled);
      worksheet.mergeCells(`${cell.address}:${cti(lastColl, currentRow)}`);
      currentRow++;
      
      // =========== description ===========
      cell = worksheet.getCell(cti(currentCol, currentRow));
      cell.value = mapQuillDeltaToRichText(textDescription)
      worksheet.mergeCells(`${cell.address}:${cti(lastColl, currentRow)}`);
      currentRow++;
    }
    
    // =========== TIMELINE ===========
    const buildTableTimeline = () => {
      currentCol = 1;
      
      worksheet.getRow(currentRow).height = TITLE_ROW_HEIGHT; // timeline row height
      
      cell = worksheet.getCell(cti(currentCol, currentRow))
      cell.value = t("proposal_parts.editor_sections.timeline");
      cell.style = proposalHeaderStyle(!unstyled);
      worksheet.mergeCells(`${cell.address}:${cti(titleCols + totalDevelopmentTime, currentRow)}`);
      currentRow++;
      
      cell = worksheet.getCell(cti(currentCol, currentRow))
      cell.value = t("views.editor.timeline_table.role");
      cell.style = tableHeaderStyle(!unstyled);
      worksheet.mergeCells(`${cell.address}:${cti(titleCols, currentRow)}`);
      currentCol += titleCols;
      
      [...new Array(totalDevelopmentTime).keys()].forEach((i) => {
        cell = worksheet.getCell(cti(currentCol, currentRow));
        cell.value = `${capitalize(t("time.month"))} ${i + 1}`;
        cell.style = tableHeaderStyle(!unstyled, true);
        currentCol++;
      });
      
      currentRow++;
      
      // =========== TIMELINE ROWS ===========
      const setTimelineValues = (element, level, displayValue, currentCol) => {
        currentCol = 1 + level;
        const chunkedTimeline = chunk(element.presentedTimeline?.weeks || [], 4);
        
        cell = worksheet.getCell(`${cti(currentCol, currentRow)}`);
        worksheet.mergeCells(`${cell.address}:${cti(titleCols, currentRow)}`);
        cell.value = element.name;
        cell.style = taskFont(!unstyled, 0, element.turnOff);
        
        currentCol += titleCols;
        
        [...new Array(totalDevelopmentTime).keys()].map((i) => {
          cell = worksheet.getCell(cti(currentCol, currentRow));
          cell.style = valueCellStyle(!unstyled, false, undefined, element.turnOff);
          const v = chunkedTimeline[i]?.reduce((t, w) => t + (w.hours || 0), 0);
          if (displayValue && v /*i >= element.timelineStartMonth && typeof v === "number"*/)
            cell.value = v
              ? roundFloat(
                v / modifier,
                Rounding.FULL,
                false, usedRoundDecimals
              )
              : 0;
          
          currentCol++;
        });
        
        currentRow++;
      };
      
      timelineWorkTypes.forEach((workType) => {
        const { isOpened, resources, hasResources } = workType;
        const displayValue = !hasResources || !isOpened;
        
        setTimelineValues(workType, 0, displayValue, currentCol);
        
        if (isOpened)
          resources?.forEach((resource) => {
            setTimelineValues(resource, 1, true, currentCol);
          });
      });
      
      // billing row
      if (isSellerOrClient) {
        currentCol = 1;
        cell = worksheet.getCell(cti(currentCol, currentRow))
        cell.value = t("views.editor.timeline_table.billing");
        cell.style = totalCellStyle(!unstyled, false, true);
        worksheet.mergeCells(`${cell.address}:${cti(titleCols, currentRow)}`);
        
        currentCol += titleCols;
        
        const chunkedTimelineBilling = chunk(timelineBilling || [], 4)
          .map(weeks => ({
            month: weeks.reduce((t, w) => t + w, 0),
            weeks,
          }));
        
        chunkedTimelineBilling.map(( { month }) => {
          cell = worksheet.getCell(cti(currentCol, currentRow));
          cell.style = totalCellStyle(!unstyled);
          
          if (month)
            cell.value = roundFloat(
              month,
              roundPrice ? 3 : 0, !roundPrice
            );
          currentCol++;
        });
        currentRow++;
      }
    };
    
    // =========== BREAKDOWN ===========
    const buildTableBreakdown = () => {
      currentCol = 1;
      
      cell = worksheet.getCell(cti(currentCol, currentRow));
      cell.value = t("proposal_parts.editor_sections.breakdown");
      cell.style = proposalHeaderStyle(!unstyled);
      worksheet.getRow(currentRow).height = TITLE_ROW_HEIGHT; // breakdown row
      worksheet.mergeCells(`${cell.address}:${cti(titleCols + totalBreakdownCols, currentRow)}`);
      currentRow++;
      
      // --------- numeration ---------
      if(showNumerationColumn) {
        cell = worksheet.getCell(cti(currentCol, currentRow));
        cell.value = "#";
        cell.style = tableHeaderStyle(!unstyled);
        worksheet.mergeCells(`${cell.address}:${cti(currentCol, currentRow + 1)}`);
        currentCol += 1;
      }
      
      // --------- module title ---------
      cell = worksheet.getCell(cti(currentCol, currentRow));
      const names = [];
      for(let i = 0; i <= viewLevel; i++)
        names.push(capitalize(t(`proposal_parts.module_levels.level_${i}`)));
      
      cell.value = names.join(" / ");
      cell.style = tableHeaderStyle(!unstyled);
      worksheet.mergeCells(`${cell.address}:${cti(titleCols, currentRow + 1)}`);
      
      currentCol = titleCols + 1;
      
      // --------- labels ---------
      if(breakdownLabelCols) {
        cell = worksheet.getCell(cti(currentCol, currentRow));
        cell.value = t("common.label");
        cell.style = tableHeaderStyle(!unstyled);
        worksheet.mergeCells(`${cell.address}:${cti(currentCol + breakdownLabelCols - 1, currentRow + 1)}`);
        currentCol += breakdownLabelCols;
      }
      
      // --------- description ---------
      if(breakdownDescriptionCols) {
        cell = worksheet.getCell(cti(currentCol, currentRow));
        cell.value = t("common.desc");
        cell.style = tableHeaderStyle(!unstyled);
        worksheet.mergeCells(`${cell.address}:${cti(currentCol + breakdownDescriptionCols - 1, currentRow + 1)}`);
        
        currentCol += breakdownDescriptionCols;
      }
      
      // --------- workload ---------
      if(breakdownEstimateCols) {
        cell = worksheet.getCell(cti(currentCol, currentRow));
        cell.value = t("views.editor.summary_table.workload");
        cell.style = tableHeaderStyle(!unstyled, true);
        worksheet.mergeCells(`${cell.address}:${cti(currentCol + breakdownEstimateCols - 1, currentRow)}`);
        
        breakdownWorkTypes.forEach((workType) => {
          const cell = worksheet.getCell(cti(currentCol, currentRow + 1));
          cell.value = workType.name;
          cell.style = tableHeaderStyle(!unstyled, true, workType.turnOff);
          if (useMinMax)
            worksheet.mergeCells(`${cell.address}:${cti(currentCol + 1, currentRow + 1)}`);
          
          currentCol += useMinMax ? 2 : 1;
        });
      }
      
      // --------- prices ---------
      if(breakdownPriceCols) {
        cell = worksheet.getCell(cti(currentCol, currentRow));
        cell.value = `${t("views.editor.price")} (${ currencyObj.symbolStart || currencyObj.symbolEnd })`;
        cell.style = tableHeaderStyle(!unstyled, true);
        worksheet.mergeCells(`${cell.address}:${cti(currentCol + breakdownPriceCols - 1, currentRow + 1)}`);
      }
      
      currentCol = 1;
      currentRow++;
      
      currentRow++;
      
      // =========== BREAKDOWN TASKS ===========
      const fillElementColumns = (
        element,
        usedWorkTypes,
        level,
        forceValueRender = false,
        drawNumerationValue = false,
      ) => {
        currentCol = 1;
        
        const renderNoValue =
          !forceValueRender &&
          element.isOpened &&
          element.displayChildren?.length &&
          level + 1 <= valueLevel;
        
        // --------- numeration ---------
        if(drawNumerationValue) {
          cell = worksheet.getCell(cti(currentCol, currentRow));
          cell.value = element.displayNumeration;
          cell.style = taskStyle(!unstyled, level, element.isTurnedOff);
          currentCol += 1;
        }
        
        currentCol += level;
        
        // --------- module title ---------
        cell = worksheet.getCell(cti(currentCol, currentRow));
        cell.value = element.name;
        cell.style = taskStyle(!unstyled, level, element.isTurnedOff);
        if(currentCol < titleCols)
          worksheet.mergeCells(`${cell.address}:${cti(titleCols, currentRow)}`);
        
        currentCol = titleCols + 1;
        
        // --------- labels ---------
        if(breakdownLabelCols) {
          cell = worksheet.getCell(cti(currentCol, currentRow));
          worksheet.mergeCells(`${cell.address}:${cti(currentCol + breakdownLabelCols - 1, currentRow)}`);
          if (element.namedLabels?.length) {
            cell.value = element.namedLabels.map(l => l.name).join(", ");
          }
          
          if(element.isTurnedOff)
            cell.style = turnedOffValueCellStyle;
          
          currentCol += breakdownLabelCols;
        }
        
        // --------- description ---------
        if(breakdownDescriptionCols) {
          sectionDescriptionsKeys.forEach(( descriptionId ) => {
            cell = worksheet.getCell(cti(currentCol, currentRow));
            worksheet.mergeCells(
              `${cell.address}:${cti(currentCol + 2, currentRow)}`
            )
            
            const content = element.descriptions?.get(descriptionId)?.content;
            if (element.hasDescriptions[descriptionId] && content)
              cell.value = mapHtmlToRichText(content);
            
            if(element.isTurnedOff)
              cell.style = turnedOffValueCellStyle;
            
            currentCol += 3;
          });
        }
        
        // --------- workload ---------
        if (breakdownEstimateCols) {
          breakdownWorkTypes.forEach((workType) => {
            cell = worksheet.getCell(cti(currentCol, currentRow));
            const cellMax = worksheet.getCell(cti(currentCol + 1, currentRow));
            currentCol += useMinMax ? 2 : 1;
            
            const skipWorkType = !usedWorkTypes.includes(workType.id);
            
            const value = roundFloat(
              element.getDisplayHoursForWorkType(workType.id),
              Rounding.FULL, false, usedRoundDecimals
            );
            const valueMax = roundFloat(
              element.getDisplayHoursMaxForWorkType(workType.id),
              Rounding.FULL, false, usedRoundDecimals
            );
            const showValueMax = displayTwoValues && value !== valueMax;
            const workTypeIsOff = element.isWorkTypeOff(workType.id);
            
            const forceCenter = renderNoValue || skipWorkType || !showValueMax;
            
            cell.style = valueCellStyle(
              !unstyled,
              level === 0,
              useMinMax && !forceCenter ? "left" : "center",
              element.isTurnedOff || workTypeIsOff
            );
            
            if (useMinMax) {
              if (forceCenter)
                worksheet.mergeCells(`${cell.address}:${cellMax.address}`);
              else
                cellMax.style = valueCellStyle(
                  !unstyled,
                  level === 0,
                  "right",
                  element.isTurnedOff || workTypeIsOff
                );
            }
            
            if (skipWorkType) return;
            
            if (!renderNoValue) {
              cell.value = value;
              if (useMinMax) {
                cellMax.value = valueMax;
              }
            }
          });
        }
        
        // --------- prices ---------
        if (breakdownPriceCols) {
          cell = worksheet.getCell(cti(currentCol, currentRow));
          cell.style = valueCellStyle(!unstyled, level === 0, useMinMax && element.showMaxPrice ? "left" : "center", element.isTurnedOff);
          const cellMax = worksheet.getCell(cti(currentCol + 1, currentRow));
          
          if (!renderNoValue) {
            cell.value = roundFloat(element.displayPrice, roundPrice ? 3 : 0, !roundPrice);
            if (useMinMax && element.showMaxPrice)
              cellMax.value = roundFloat(element.displayPriceMax, roundPrice ? 3 : 0, !roundPrice);
          }
          
          
          if (useMinMax) {
            if (element.showMaxPrice)
              cellMax.style = valueCellStyle(!unstyled, level === 0, "right", element.isTurnedOff);
            else
              worksheet.mergeCells(`${cell.address}:${cellMax.address}`);
          }
        }
      };
      
      const renderModule = (task, usedWorkTypes, level = 1, drawNumerationValue) => {
        fillElementColumns(task, usedWorkTypes, level, false, drawNumerationValue);
        
        currentRow++;
        
        // --------- submodules ---------
        if (task.isOpened && task.displayChildren?.length) {
          task.displayChildren?.forEach((child) =>
            renderModule(child, usedWorkTypes, level + 1, drawNumerationValue)
          );
        }
      };
      
      // =========== BREAKDOWN SECTIONS ===========
      displaySections.forEach((section, i) => {
        fillElementColumns(
          section,
          section.usedWorkTypes,
          0,
          true,
          section.showNumerationColumn,
        );
        
        currentRow++;
        
        // --------- tasks ---------
        
        section.displayChildren?.forEach((task) =>
          renderModule(task, section.usedWorkTypes, 1, section.showNumerationColumn)
        );
        
        // --------- spacing row ---------
        if (i + 1 < displaySections.length) {
          cell = worksheet.getCell(cti(1, currentRow));
          cell.style = dividerStyle(!unstyled);
          worksheet.mergeCells(`${cell.address}:${cti(titleCols + totalBreakdownCols, currentRow)}`);
          
          currentRow++;
        }
      });
    };
    
    visibleTables.forEach(({ name }) => {
      switch (name) {
        case Tables.SUMMARY:
          buildTableSummary();
          break;
        case Tables.TIMELINE:
          buildTableTimeline();
          break;
        case Tables.BREAKDOWN:
          buildTableBreakdown();
          break;
        case Tables.DESCRIPTION:
          buildTextDescription();
          break;
      }
      currentRow += 2;
    });
    
    // =========== EXPORT & DOWNLOAD ===========
    workbook.xlsx
      .writeBuffer({
        base64: true,
      })
      .then(function (xls64) {
        const data = new window.Blob([xls64], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        
        let name = "export";
        
        if(estimateUuid)
          name = estimateUuid;
        if(company && project)
          name = `${company}-${project}`;
        
        downloadFile(
          getProposalExportFileName(name, "xlsx"),
          data
        );
      })
      .catch(function (error) {
        console.error(error.message);
      });
    
    setLoading(false);
  };
  
  return {
    isLoading,
    exportProposal,
  };
};

function cti(colIndex, rowIndex) {
  if (!colIndex && colIndex !== 0) return rowIndex || 0;
  colIndex = Math.floor(Math.abs(colIndex));
  let col = "",
    rest;
  while (colIndex > 0) {
    rest = (colIndex - 1) % 26;
    col = String.fromCharCode(65 + rest) + col;
    colIndex = parseInt((colIndex - rest) / 26);
  }
  return rowIndex || rowIndex === 0 ? col + rowIndex : col;
}